/**
 * Single post-clock frame bus so 2D playback consumers run in the same rAF
 * as the simulation clock (HTML-player style: advance → transfers → paint).
 *
 * Phases:
 *  - schedule: resolve every per-OHT typed queue
 *  - replay: derive shared station / FOUP replay state
 *  - transfers: turntables / connectors (writes railTransferPoseStore)
 *  - paint: OHT Konva transforms (reads poses after transfers)
 */
export type FramePhase = 'schedule' | 'replay' | 'transfers' | 'paint';

type FrameListener = (simTime: number, wallDtS: number) => void;

class SimulationFrameBus {
  private schedule = new Set<FrameListener>();
  private replay = new Set<FrameListener>();
  private transfers = new Set<FrameListener>();
  private paint = new Set<FrameListener>();

  subscribe(listener: FrameListener, phase: FramePhase = 'paint'): () => void {
    const set =
      phase === 'schedule'
        ? this.schedule
        : phase === 'replay'
          ? this.replay
          : phase === 'transfers'
            ? this.transfers
            : this.paint;
    set.add(listener);
    return () => {
      set.delete(listener);
    };
  }

  /** Called once per clock tick after simulationClockStore.setTime. */
  emit(simTime: number, wallDtS: number) {
    for (const listener of this.schedule) {
      listener(simTime, wallDtS);
    }
    for (const listener of this.replay) {
      listener(simTime, wallDtS);
    }
    for (const listener of this.transfers) {
      listener(simTime, wallDtS);
    }
    for (const listener of this.paint) {
      listener(simTime, wallDtS);
    }
  }
}

export const simulationFrameBus = new SimulationFrameBus();
